import { ChangeDetectionStrategy, Component, ElementRef, Input, effect, forwardRef, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { styleInput } from '@contler/models/ui';

@Component({
  selector: 'ctr-input',
  imports: [FormsModule, ReactiveFormsModule],
  templateUrl: './input.component.html',
  styleUrl: './input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => InputComponent), multi: true }],
})
export class InputComponent implements ControlValueAccessor {
  /**
   * The type of input to be displayed.
   */
  @Input() inputType: 'text' | 'number' | 'email' | 'password' | 'date' | 'time' | 'tel' | 'url' = 'text';

  /**
   * The hint text to be displayed.
   */
  @Input() hint?: string;

  /**
   * The label text to be displayed.
   */
  @Input() label?: string;

  /**
   * the style it will have.
   */
  @Input() style: styleInput = 'outline';

  /**
   * The name of the input.
   */
  @Input() name?: string;

  /**
   * Indicates whether the input is disabled or not.
   */
  isDisabled = false;

  /*
   * The form control of the input.
   */
  public control = new FormControl();

  /*
   * An observable of the value of the input.
   */
  private value$ = this.control.valueChanges;

  /*
   * The signal value of the input.
   */
  public valueSignal = toSignal(this.value$);

  public inputRef = viewChild<ElementRef>('input');

  private onChange?: (value: string) => void;
  private onTouched?: () => void;

  constructor() {
    effect(() => {
      const value = this.valueSignal();
      if (this.inputType === 'number' && typeof value === 'string') {
        this.control.setValue(value?.replace(/\D/g, ''), { emitEvent: false });
        this.onChange?.(value?.replace(/\D/g, ''));
      } else if (this.inputType === 'tel' && typeof value === 'string') {
        this.control.setValue(value?.replace(/[^\d+]/g, ''), { emitEvent: false });
        this.onChange?.(value?.replace(/[^\d+]/g, ''));
      } else if (value || value === '') {
        this.onChange?.(value);
      }
    });
  }

  /**
   * Writes a new value to the input.
   * @param obj The new value to be set.
   */
  writeValue(obj: string): void {
    this.control.setValue(obj);
  }

  /**
   * Registers a callback function to be called when the input value changes.
   * @param fn The callback function to be registered.
   */
  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  /**
   * Registers a callback function to be called when the input is touched.
   * @param fn The callback function to be registered.
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * Sets the disabled state of the input.
   * @param isDisabled Indicates whether the input should be disabled or not.
   */
  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }
  }

  /**
   * Handles the blur event of the input component.
   */
  onBlur() {
    this.onTouched?.();
  }
}
